import type { Asset, Draft, PaintOrder, Placement, Surface } from '../shared/types';
import { pendingLayerIndex, sortPaintedLayers } from '../shared/paintOrder';
import {clipSurfaceCanvas} from './surfaceCanvasClip';
import {isCurrentBoardImage} from './boardRoute';
import {eraseEventsFor} from '../shared/freestyleErase';

export interface WallArtworkPlan {
  key: string; surface: Surface; width: number; height: number;
  layers: { placement: Placement; asset: Asset }[];
  missing: boolean;
  pendingIndex: number;
  eraseLayers?:{placement:Placement;asset:Asset}[];
}

/** Only confirmed artwork participates. Coordinates are local to the wall, never the camera. */
export function wallArtworkPlan(surface: Surface, placements: Placement[], assets: Asset[], width: number, height: number, position: Pick<Draft, 'paintOrder' | 'belowPlacementId'> = {}): WallArtworkPlan {
  if (!Number.isInteger(width) || !Number.isInteger(height) || width < 1 || height < 1 || width * height > 4_194_304 || !(surface.width > 0 && surface.height > 0)) throw new Error('Invalid wall artwork dimensions.');
  const byId = new Map(assets.map(asset => [asset.id, asset]));
  const original = placements.filter(mark => mark.surfaceId === surface.id&&mark.operation!=='erase');
  const rows = sortPaintedLayers(original).filter(mark=>!mark.removed);
  const layers: WallArtworkPlan['layers'] = [];
  let missing = false;
  for (const placement of rows) {
    const asset = byId.get(placement.assetId);
    if (!asset) missing = true;
    else layers.push({ placement, asset });
  }
  const eraseLayers=placements.filter(mark=>mark.surfaceId===surface.id&&mark.operation==='erase').flatMap(placement=>{const asset=byId.get(placement.assetId);if(!asset){missing=true;return [];}return [{placement,asset}];});
  const pendingIndex = pendingLayerIndex(original, position);
  return { surface, width, height, layers, missing, pendingIndex,eraseLayers,
    key: JSON.stringify([eraseLayers.map(({placement,asset})=>[placement.id,placement.eraseCutoff,asset.url]),surface.id, surface.width, surface.height, surface.regions ?? null, surface.contours ?? null, surface.reservePanels ?? null, surface.panelReservations ?? null, surface.exteriorClip ?? null, surface.exteriorOcclusions ?? null, surface.role ?? 'panel', width, height, position.paintOrder ?? 'over', position.belowPlacementId ?? null, rows.map(mark => [mark.id, mark.sequence, mark.paintOrder ?? 'over', mark.belowPlacementId ?? null, mark.x, mark.y, mark.width, mark.height, mark.rotation ?? 0, mark.opacity ?? 1, byId.get(mark.assetId)?.url ?? null])]) };
}

export interface WallImage { source: CanvasImageSource; release(): void }
export type WallImageLoader = (url: string, signal: AbortSignal) => Promise<WallImage>;
export interface WallArtworkBackdrop { below: HTMLCanvasElement; above: HTMLCanvasElement }

/** Freeze one tap's visible boundaries; exported artwork still contains only the draft. */
export function snapshotWallFill(backdrop: HTMLCanvasElement | WallArtworkBackdrop, draft: HTMLCanvasElement, paintOrder: PaintOrder = 'over'): ImageData {
  const layers = 'below' in backdrop ? [backdrop.below, draft, backdrop.above] : paintOrder === 'under' ? [draft, backdrop] : [backdrop, draft];
  if (layers.some(layer => layer.width !== draft.width || layer.height !== draft.height)) throw new Error('Wall artwork is updating. Try the fill again.');
  const reference = document.createElement('canvas');
  reference.width = draft.width; reference.height = draft.height;
  try {
    const context = reference.getContext('2d', { willReadFrequently: true });
    if (!context) throw new Error('Your browser could not read the wall artwork.');
    for (const layer of layers) context.drawImage(layer, 0, 0);
    return context.getImageData(0, 0, reference.width, reference.height);
  } finally { reference.width = 0; reference.height = 0; }
}

/** Decode each saved image once, with two transparent rasters surrounding the
 * draft. The visible reference is below → draft → above, even in a deep stack. */
export async function renderWallArtworkBackdrop(plan: WallArtworkPlan, backdrop: WallArtworkBackdrop, load: WallImageLoader, signal: AbortSignal): Promise<WallArtworkBackdrop> {
  await renderWallArtwork({ ...plan, layers: plan.layers.slice(0, plan.pendingIndex) }, backdrop.below, load, signal);
  await renderWallArtwork({ ...plan, layers: plan.layers.slice(plan.pendingIndex) }, backdrop.above, load, signal);
  return backdrop;
}

/** Sequential decoding bounds memory to one image plus one wall raster. Never draw missing art as blank. */
export async function renderWallArtwork(plan: WallArtworkPlan, canvas: HTMLCanvasElement, load: WallImageLoader, signal: AbortSignal): Promise<HTMLCanvasElement> {
  if (plan.missing) throw new Error('Some saved artwork is still loading.');
  canvas.width = plan.width; canvas.height = plan.height;
  const context = canvas.getContext('2d');
  if (!context) throw new Error('Your browser could not read the wall artwork.');
  context.scale(plan.width / plan.surface.width, plan.height / plan.surface.height);
  clipSurfaceCanvas(context,plan.surface);
  for (const { placement, asset } of plan.layers) {
    signal.throwIfAborted();
    const image = await load(asset.url, signal);
    try {
      signal.throwIfAborted();
      const erases=(plan.eraseLayers??[]).filter(event=>eraseEventsFor(placement,[event.placement]).length);
      if(!erases.length)draw(context,placement,image.source);
      else{
        const layer=document.createElement('canvas');layer.width=plan.width;layer.height=plan.height;
        try{
          const layerContext=layer.getContext('2d');if(!layerContext)throw new Error('Paint visibility could not be prepared.');
          layerContext.scale(plan.width/plan.surface.width,plan.height/plan.surface.height);clipSurfaceCanvas(layerContext,plan.surface);draw(layerContext,placement,image.source);
          for(const event of erases){signal.throwIfAborted();const mask=await load(event.asset.url,signal);try{layerContext.globalCompositeOperation='destination-out';draw(layerContext,event.placement,mask.source);}finally{mask.release();}}
          context.drawImage(layer,0,0,plan.surface.width,plan.surface.height);
        }finally{layer.width=0;layer.height=0;}
      }
    } finally { image.release(); }
  }
  signal.throwIfAborted();
  return canvas;
}

export const loadWallImage: WallImageLoader = (url, signal) => new Promise((resolve, reject) => {
  if (!isCurrentBoardImage(url)) { reject(new Error('Saved artwork has an unavailable image.')); return; }
  if (signal.aborted) { reject(signal.reason); return; }
  const image = new Image();
  const cleanup = () => { clearTimeout(timer); signal.removeEventListener('abort', abort); image.onload = null; image.onerror = null; };
  const fail = (error: unknown) => { cleanup(); image.src = ''; reject(error); };
  const abort = () => fail(signal.reason);
  const timer = setTimeout(() => fail(new Error('Saved artwork took too long to load. Retry when connected.')), 20_000);
  signal.addEventListener('abort', abort, { once: true });
  image.onload = () => {
    cleanup();
    if (!image.naturalWidth || !image.naturalHeight || image.naturalWidth * image.naturalHeight > 4_194_304) { image.src = ''; reject(new Error('Saved artwork dimensions are unavailable.')); return; }
    resolve({ source: image, release: () => { image.src = ''; } });
  };
  image.onerror = () => fail(new Error('Saved artwork could not load. Your drawing is safe; retry the wall fill.'));
  image.src = url;
});

function draw(context:CanvasRenderingContext2D,placement:Placement,source:CanvasImageSource){
  context.save();context.globalAlpha=placement.opacity??1;context.translate(placement.x+placement.width/2,placement.y+placement.height/2);context.rotate((placement.rotation??0)*Math.PI/180);context.drawImage(source,-placement.width/2,-placement.height/2,placement.width,placement.height);context.restore();
}
